/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import DetectBrowser from 'phenix-web-detect-browser';
import TelemetryUrl from 'services/telemetry/TelemetryUrl';
import TelemetryConfiguration from 'services/telemetry/TelemetryConfiguration';
import TelemetryAppender from 'services/telemetry/TelemetryApender';
import hostService from 'services/host-url.service';
import userStore from 'services/user-store';
import {ILogger} from './LoggerInterface';
import Logger, {LoggingLevel} from './Logger';
import Appenders from './Appenders';
import LoggingThreshold from './LoggingThreshold';
import ConsoleAppender from './ConsoleAppender';
import LoggerDefaults from './LoggerDefaults';

export default class LoggerFactory {
  private static _loggers: {[category: string]: ILogger} = {};
  private static _appenders: Appenders = new Appenders();
  private static _threshold: LoggingThreshold = new LoggingThreshold();
  private static _telemetryConfiguration: TelemetryConfiguration = new TelemetryConfiguration();

  static get telemetryConfiguration(): TelemetryConfiguration {
    return this._telemetryConfiguration;
  }

  static applyLoggerConfig(): void {
    LoggerFactory.applyConsoleLogger(LoggingLevel['All']);
    LoggerFactory.applyLoggingLevel();
    LoggerFactory.applyTelemetryLogger();
  }

  static getLogger(category: string): ILogger {
    if (typeof category !== 'string') {
      category = 'portal';
    }

    const logger = LoggerFactory._loggers[category];

    if (logger) {
      return logger;
    }

    return LoggerFactory._loggers[category] = new Logger(category, this._appenders, this._threshold);
  }

  static applyLoggingLevel(): void {
    this._threshold.setThreshold(LoggingLevel['All']);
  }

  static applyConsoleLogger(level: LoggingLevel): void {
    this._appenders.add(new ConsoleAppender(level || LoggerDefaults.defaultConsoleLoggingLevel));
  }

  static async applyTelemetryConfiguration(level: LoggingLevel): Promise<void> {
    const browser = new DetectBrowser(navigator.userAgent).detect();

    this._telemetryConfiguration.threshold = level || LoggerDefaults.defaultTelemetryLoggingLevel;
    this._telemetryConfiguration.url = TelemetryUrl.getTelemetryUrl();
    this._telemetryConfiguration.environment = hostService.getHostUrl();
    this._telemetryConfiguration.tenancy = await userStore.get('applicationId');
    this._telemetryConfiguration.userId = await userStore.get('applicationId');
    this._telemetryConfiguration.sessionId = await userStore.get('sessionId');
    this._telemetryConfiguration.browser = browser ? `${browser.browser}/${browser.version}` : null;
  }

  private static applyTelemetryLogger(): void {
    LoggerFactory.applyTelemetryConfiguration(LoggingLevel['Info']);

    this._appenders.add(new TelemetryAppender(this._telemetryConfiguration));
  }

  private constructor() {
    throw new Error('LoggerFactory is a static class that may not be instantiated');
  }
}

LoggerFactory.applyLoggerConfig();